import { mapState, mapActions } from 'vuex';
import { email, required } from 'vuelidate/lib/validators';
import { apiCallers } from '@/api/apiRequest';
import { formatMoney } from '@/modules/utils';
import { downloadBase64, downloadBlob, getFileNameFromHeader } from '@/utils/download';

import moment from 'moment';

import NCustomSelect from'@/components/common/n-custom-select.vue';
import NCheckbox from'@/components/common/n-checkbox.vue';
import NRadio from '@/components/common/n-radio.vue';
import NText from'@/components/common/n-text.vue';
import VkbDatePicker from '@/components/common/vkb-date-picker';

import ScrollMixin from '@/mixins/scrollHandler.js';
import ThemeMixin from '@/mixins/themeHandler.js';
import TrackingMixin from '@/mixins/trackingHandler.js';

export default {
  components: {
    NCustomSelect,
    NCheckbox,
    NText,
    NRadio,
    VkbDatePicker
  },

  mixins: [
    ScrollMixin,
    ThemeMixin,
    TrackingMixin
  ],

  data: () => ({
    loading: false,
    show_hint: false,
    current_step: 1,
    passed_step: 1,

    internship: {
      type: '', // 1: individual, 2: semester
      date_from: '',
      date_to: '',
    },

    policyholder: {
      gender: '', // 1: male, 2: female
      forename: '',
      surename: '',
      birthday: '',
      plz: '',
      city: '',
      street: '',
      street_nr: '',
      email: '',
      telephone: ''
    },

    company: {
      b_name: '',
      b_plz: '',
      b_city: '',
      b_street: '',
      b_street_nr: '',
      // b_email: '',
      b_telephone: ''
    },

    agreement: {
      confirm_risiko: false,
      confirm_sonstig: false,
      confirm_widerrufsrecht: false,
      confirm_beratungsfreiheit: false
    },

    calc_result: null,
    user_info: null,

    confirm_download: false,
    document_id: 0,
    document_token: 0,

    internship_types: [
      {
        value: 'p',
        text: 'Individuelles Betriebspraktikum'
      },
      {
        value: 's',
        text: 'Theoretisches oder praktisches Semester an einer Hochschule in Bayern'
      }
    ],

    genders: [
      {
        value: 'm',
        text: 'Herr'
      },
      {
        value: 'f',
        text: 'Frau'
      }
    ],

    min_date_from: moment().add(1, 'day').format('DD.MM.YYYY'),
    max_date: moment().add(1, 'year').format('[31.12.]YYYY'),

    steps: [
      {
        id: 1,
        text: 'Praktikum'
      },
      {
        id: 2,
        text: 'Daten'
      },
      {
        id: 3,
        text: 'Zusammenfassung'
      },
      {
        id: 4,
        text: 'Einwilligungen'
      },
      {
        id: 5,
        text: 'Bestätigung'
      }
    ],

    policyholder_inputs: [
      {
        id: 'forename',
        text: 'Nachname',
        required: true
      },
      {
        id: 'surename',
        text: 'Vorname',
        required: true
      },
      {
        id: 'birthday',
        text: 'Geburtsdatum',
        required: true,
        hide: false
      },
      {
        id: 'plz',
        text: 'Postleitzahl',
        required: true,
        hide: false
      },
      {
        id: 'city',
        text: 'Ort',
        required: true,
        hide: false
      },
      {
        id: 'street',
        text: 'Straße',
        required: true,
        hide: false
      },
      {
        id: 'street_nr',
        text: 'Hausnummer',
        required: true,
        hide: false
      },
      {
        id: 'email',
        text: 'E-Mail-Adresse',
        required: true,
        hide: false
      },
      {
        id: 'telephone',
        text: 'Telefon',
        required: false,
        hide: false
      }
    ],

    company_inputs: [
      {
        id: 'b_plz',
        text: 'Postleitzahl',
        required: true,
        hide: false
      },
      {
        id: 'b_city',
        text: 'Ort',
        required: true,
        hide: false
      },
      {
        id: 'b_street',
        text: 'Straße',
        required: true,
        hide: false
      },
      {
        id: 'b_street_nr',
        text: 'Hausnummer',
        required: true,
        hide: false
      },
      // {
      //   id: 'b_email',
      //   text: 'E-Mail-Adresse',
      //   required: false,
      //   hide: false
      // },
      {
        id: 'b_telephone',
        text: 'Telefon',
        required: false,
        hide: false
      }
    ]
  }),

  computed: {
    ...mapState([
      'appConfigs',
      'internshipState'
    ]),

    apiRequest () {
      return apiCallers[this.config.app];
    },

    config () {
      return this.appConfigs[this.$options.configIndex] || {};
    },

    title () {
      return this.internship.type === 'p'
        ? 'Haftpflicht- und Unfallschutz für Betriebspraktika'
        : 'Haftpflichtschutz für Hochschulsemester';
    },

    error_messages() {
      return {
        gender: {
          required: 'Bitte treffen Sie für Ihre Anrede eine Auswahl.'
        },

        forename: {
          required: 'Bitte geben Sie Ihren Vornamen ein.'
        },

        surename: {
          required: 'Bitte geben Sie Ihren Nachnamen ein.'
        },

        birthday: {
          required: 'Bitte geben Sie Ihr Geburtsdatum ein oder nutzen das Kalendersymbol zur Auswahl.'
        },

        plz: {
          required: 'Bitte geben Sie die Postleitzahl Ihres Wohnorts ein.',
          length: 'Bitte geben Sie die fünfstellige Postleitzahl Ihres Wohnorts, ausschließlich bestehend aus Ziffern, ein.'
        },

        city: {
          required: 'Bitte geben Sie den Namen Ihres Wohnorts ein.'
        },

        street: {
          required: 'Bitte geben Sie Ihre Straße ein.'
        },

        street_nr: {
          required: 'Bitte geben Sie Ihre Hausnummer ein.'
        },

        email: {
          required: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
          email: 'Bitte geben Sie Ihre gültige E-Mail-Adresse ein.'
        },

        b_name: {
          required: this.internship.type === 'p'
            ? 'Bitte geben Sie den Namen Ihres Betriebs ein.'
            : 'Bitte geben Sie den Namen Ihrer Hochschule ein.',
        },

        b_plz: {
          required: this.internship.type === 'p'
            ? 'Bitte geben Sie die Postleitzahl Ihres Betriebs ein.'
            : 'Bitte geben Sie die Postleitzahl Ihrer Hochschule ein.',
          length: this.internship.type === 'p'
            ? 'Bitte geben Sie die fünfstellige Postleitzahl Ihres Betriebs, ausschließlich bestehend aus Ziffern, ein.'
            : 'Bitte geben Sie die fünfstellige Postleitzahl Ihres Hochschule, ausschließlich bestehend aus Ziffern, ein.'
        },

        b_city: {
          required: this.internship.type === 'p'
            ? 'Bitte geben Sie den Ort Ihres Betriebs ein.'
            : 'Bitte geben Sie den Ort Ihrer Hochschule ein.'
        },

        b_street: {
          required: this.internship.type === 'p'
            ? 'Bitte geben Sie die Straße Ihres Betriebs ein.'
            : 'Bitte geben Sie die Straße Ihrer Hochschule ein.'
        },

        b_street_nr: {
          required: this.internship.type === 'p'
            ? 'Bitte geben Sie die Hausnummer Ihres Betriebs ein.'
            : 'Bitte geben Sie die Hausnummer Ihrer Hochschule ein.'
        },

        // b_email: {
        //   email: 'Bitte geben Sie Ihre gültige E-Mail-Adresse ein.'
        // }
      };
    },

    calc_value () {
      if (!this.calc_result) {
        return 0;
      }

      return formatMoney(this.calc_result.value_euro, 2);
    },

    contract_term () {
      if (!this.internship.date_from || !this.internship.date_to) {
        return '';
      }

      return `${ this.internship.date_from } bis ${ this.internship.date_to }`;
    },

    contract_weeks () {
      if (!this.calc_result) {
        return '';
      }

      return `${ this.calc_result.number_weeks } angebrochene Wochen`;
    },

    downloadable () {
      return (
        this.agreement.confirm_risiko &&
        this.agreement.confirm_widerrufsrecht &&
        this.agreement.confirm_beratungsfreiheit
      );
    },

    max_date_from () {
      const max_date = !this.internship.date_to
        ? this.max_date
        : this.internship.date_to;

      return this.formatDateFromOffset(max_date, -1);
    },

    min_date_to () {
      const min_date = !this.internship.date_from
        ? this.min_date_from
        : this.internship.date_from;

      return this.formatDateFromOffset(min_date, 1);
    },

    default_birthday () {
      return this.formatDateFromOffset(moment().format('DD.MM.YYYY'), -18, 'years');
    }
  },

  created () {
    this.initInternshipData();
  },

  methods: {
    ...mapActions([
      'setInternshipState',
      'resetInternshipState'
    ]),

    initInternshipData () {
      this.current_step = 1;
      this.passed_step = 1;

      this.internship = {
        type: '', // 1: individual, 2: semester
        date_from: '',
        date_to: '',
      };

      this.policyholder = {
        gender: '', // 1: male, 2: female
        forename: '',
        surename: '',
        birthday: '',
        plz: '',
        city: '',
        street: '',
        street_nr: '',
        email: '',
        telephone: ''
      };

      this.company = {
        b_name: '',
        b_plz: '',
        b_city: '',
        b_street: '',
        b_street_nr: '',
        // b_email: '',
        b_telephone: ''
      };

      this.agreement = {
        confirm_risiko: false,
        confirm_sonstig: false,
        confirm_widerrufsrecht: false,
        confirm_beratungsfreiheit: false
      };

      this.calc_result = null;
      this.user_info = null;

      this.confirm_download = false;
      this.document_id = 0;
      this.document_token = 0;

      if (this.internshipState.current_step) {
        this.current_step = this.internshipState.current_step;
      }

      if (this.internshipState.passed_step) {
        this.passed_step = this.internshipState.passed_step;
      }

      if (this.internshipState.internship) {
        this.internship = {
          ...this.internshipState.internship
        };

        this.loadCalcResult();
      }

      if (this.internshipState.policyholder) {
        this.policyholder = {
          ...this.internshipState.policyholder
        };
      }

      if (this.internshipState.company) {
        this.company = {
          ...this.internshipState.company
        };
      }

      if (this.internshipState.agreement) {
        this.agreement = {
          ...this.internshipState.agreement
        };
      }

      if (this.internshipState.document_id) {
        this.document_id = this.internshipState.document_id;
      }

      if (this.internshipState.document_token) {
        this.document_token = this.internshipState.document_token;
      }

      this.loadUserInfo();
      this.triggerTransitionEvent('abschluss', 1, 'praktikum');
    },

    formatDate (value) {
      if (!value) {
        return '';
      }

      const date_ = moment(value, 'DD.MM.YYYY');

      return date_.isValid()
        ? date_.format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
        : '';
    },

    formatDateFromOffset (value, offset = 1, unit = 'days') {
      if (!value) {
        return '';
      }

      const date_ = moment(value, 'DD.MM.YYYY').add(offset, unit);

      return date_.isValid()
        ? date_.format('DD.MM.YYYY')
        : '';
    },

    getErrorMessage (form, field = null) {
      const validity = field
        ? this.$v[form][field]
        : this.$v[form];

      if (!validity || !validity.$dirty) {
        return;
      }

      const error_key = Object.keys(validity.$params).find(k => validity[k] === false);

      return error_key
        ? this.error_messages[field][error_key]
        : '';
    },

    loadUserInfo () {
      this.apiRequest
        .get(this.config.paths.info)
        .then(res => {
          this.user_info = res.data;
        });
    },

    loadCalcResult () {
      if (!this.internship.date_from || !this.internship.date_to) {
        this.calc_result = null;
        return;
      }

      const params = {
        type: this.internship.type,
        date_from: this.formatDate(this.internship.date_from),
        date_to: this.formatDate(this.internship.date_to)
      };

      this.apiRequest
        .get(this.config.paths.calc, { params })
        .then(res => {
          this.calc_result = res.data;
        })
        .catch(() => {
          this.calc_result = null;
        });
    },

    confirmDownload () {
      return this.apiRequest
        .post(this.config.paths.confirm_download, {
          id: parseInt(this.document_id),
          token: this.document_token
        })
        .catch(() => {
          this.document_id = 0;
          this.document_token = '';
        });
        // .finally(() => this.setInternshipState({
        //   document_id: this.document_id,
        //   document_token: this.document_token
        // }));
    },

    clickToElement (id) {
      setTimeout(() => {
        const a = document.createElement('a');
        document.body.appendChild(a);

        a.style.display = 'none';
        a.href = id;
        a.click();

        document.body.removeChild(a);
      }, 0);
    },

    closeOtherPicker(name) {
      const date_picker = this.$refs[name];

      if (date_picker) {
        date_picker.onCloseDatePicker();
      }
    },

    onPrevStep () {
      this.current_step = this.current_step > 1
        ? this.current_step - 1
        : 1;

      this.clickToElement('#top-nav');
    },

    onNextStep () {
      if (this.current_step === 1) {
        this.$v.internship.$touch();
        this.triggerValidationEvent(this.$v.internship, ['date_from', 'date_to'], this.error_messages);

        if (this.$v.internship.$invalid) {
          return;
        }
      }

      if (this.current_step === 2) {
        this.$v.policyholder.$touch();
        this.$v.company.$touch();

        const validations = {
          ...this.$v.policyholder,
          ...this.$v.company
        };

        const trackingKeys = [
          ...Object.keys(this.policyholder),
          ...Object.keys(this.company)
        ];

        this.triggerValidationEvent(validations, trackingKeys, this.error_messages);

        if (this.$v.policyholder.$invalid || this.$v.company.$invalid) {
          return;
        }
      }

      if (this.current_step === 4) {
        this.confirmDownload();
      }

      this.current_step = this.current_step < this.steps.length
        ? this.current_step + 1
        : this.steps.length;

      if (this.current_step > this.passed_step) {
        this.passed_step = this.current_step;
      }

      this.clickToElement('#top-nav');

      if (this.current_step === 5) {
        // remove vuex state
        // console.log('current_step: ', this.current_step);
        this.resetInternshipState();
      }
    },

    onSelectStep (id, link = '') {
      if (!link) {
        if (id >= 4 || this.document_id) {
          return;
        }

        if (this.passed_step < id || this.current_step >= 4) {
          return;
        }

        if (this.current_step === 1 && !this.calc_result) {
          return;
        }

        if (this.current_step === 2 && id === 3) {
          this.$v.policyholder.$touch();
          this.$v.company.$touch();

          if (this.$v.policyholder.$invalid || this.$v.company.$invalid) {
            return;
          }
        }
      }

      this.current_step = id;

      if (link) {
        this.clickToElement(link);
      }
    },

    onDownloadTerms () {
      this.triggerActionEvent('downloadProduktinformationen (submit)');

      const params = {
        ...this.internship,
        ...this.policyholder,
        ...this.company,
        ...this.agreement
      };

      params.date_from = this.formatDate(params.date_from);
      params.date_to = this.formatDate(params.date_to);
      params.birthday = this.formatDate(params.birthday);

      this.loading = true;

      this.apiRequest
        .post(this.config.paths.save_first, params)
        .then(res => {
          downloadBase64(res.data.pdf_file_data, res.data.pdf_filename);

          this.document_id = res.data.document_id;
          this.document_token = res.data.document_token;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onDownloadInsurance () {
      this.triggerActionEvent('downloadVersicherungsschein (submit)');

      const params = {
        id: parseInt(this.document_id),
        token: this.document_token
      };

      this.loading = true;

      this.apiRequest
        .post(this.config.paths.download_insurance, params, {
          responseType: 'blob'
        })
        .then(res => {
          downloadBlob(res.data, getFileNameFromHeader(res.headers));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onContactClick () {
      this.triggerActionEvent('angebotAnfordern (submit)');

      if (this.config.contact_url) {
        window.open(this.config.contact_url, '_top');
        return;
      }

      window.open('https://www.vkb.de/content/versicherungen/haus-wohnen/wohngebaeudeversicherung/beraten-anfragen/', '_blank');
    },

    onResetStep () {
      this.resetInternshipState()
        .then(() => {
          this.initInternshipData();
        });
    },

    formatMoney
  },

  validations: {
    internship: {
      date_from: {
        required
      },

      date_to: {
        required
      }
    },

    policyholder: {
      gender: {
        required
      },

      forename: {
        required
      },

      surename: {
        required
      },

      birthday: {
        required
      },

      plz: {
        required,
        length: (v) => !v || v.length === 5
      },

      city: {
        required
      },

      street: {
        required
      },

      street_nr: {
        required
      },

      email: {
        required,
        email
      },
    },

    company: {
      b_name: {
        required
      },

      b_plz: {
        required,
        length: (v) => !v || v.length === 5
      },

      b_city: {
        required
      },

      b_street: {
        required
      },

      b_street_nr: {
        required
      },

      // b_email: {
      //   email
      // }
    },
  },

  watch: {
    current_step: {
      handler() {
        if (this.current_step < 5) {
          this.setInternshipState({
            current_step: this.current_step
          });
        }

        const moduleSteps = [
          'praktikum',
          'daten',
          'zusammenfassung',
          'einwilligungen',
          'bestaetigung'
        ];

        this.triggerTransitionEvent('abschluss', this.current_step, moduleSteps[this.current_step - 1]);

        if (this.current_step === 5) {
          this.triggerActionEvent('kostenpflichtigAbschliessen (submit)');
        }
      }
    },

    passed_step: {
      handler() {
        if (this.passed_step < 5) {
          this.setInternshipState({
            passed_step: this.passed_step
          });
        }
      }
    },

    internship: {
      deep: true,
      handler () {
        this.setInternshipState({
          internship: this.internship
        });
      }
    },

    policyholder: {
      deep: true,
      handler () {
        this.setInternshipState({
          policyholder: this.policyholder
        });
      }
    },

    company: {
      deep: true,
      handler () {
        this.setInternshipState({
          company: this.company
        });
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/apps/internship/logic.js